'use client'

import { useState, useMemo, useEffect, useCallback, useRef } from 'react'

import _ from 'lodash'
import Stories from 'react-insta-stories'

import { LevelFooter } from '../Footer'
import { HUD } from '../HUD'
import { StoryPlaybackControl } from './StoryPlaybackControl'

import type { Challenge } from '@/features/journeys'
import { useLevelContext } from '@/features/level-play'
import { resolveStoriesPlaybackPaused } from '@/features/level-play/domain/resolveStoriesPlaybackPaused'
import { ChallengeStep, useFlow } from '@/shared/lib/steps-flow'
import { showErrorAlert } from '@/shared/lib/ui'
import { Button } from '@/shared/ui/Button'

interface GameStoriesProps {
  step: ChallengeStep
  challengeId: number
}

interface Story {
  kind: 'text' | 'image' | 'video'
  text?: string
  image?: { file?: { url?: string } }
  video?: { file?: { url?: string } }
}

export function GameStories({ step, challengeId }: GameStoriesProps) {
  const { goNext, goPrev } = useFlow()
  const {
    challenges,
    onChallengeAnswer,
    getMergedStoryRunMinimumAdvanceStatus
  } = useLevelContext()

  const mergedRunIds = step.mergedStoryChallengeIds
  const isMergedRun = (mergedRunIds?.length ?? 0) > 1

  const orderedChallenges = useMemo(() => {
    if (isMergedRun && mergedRunIds) {
      return mergedRunIds
        .map(id => challenges.find(c => c.id === id))
        .filter((c): c is Challenge => c != null)
    }
    const found = challenges.find(c => c.id === challengeId)
    return found ? [found] : []
  }, [challenges, challengeId, isMergedRun, mergedRunIds])

  const displayChallenge = orderedChallenges[0]

  const minimumAdvance = isMergedRun
    ? getMergedStoryRunMinimumAdvanceStatus(mergedRunIds ?? [])
    : { isEnabled: false, isLocked: false, remainingSeconds: 0, label: '' }

  const perScreenMinSeconds = isMergedRun
    ? Math.max(0, ...orderedChallenges.map(c => c.minimum_time_to_advance_in_seconds ?? 0))
    : (orderedChallenges[0]?.minimum_time_to_advance_in_seconds ?? 0)

  const [screenStartedAtMs, setScreenStartedAtMs] = useState(() => Date.now())
  const [screenNowMs, setScreenNowMs] = useState(() => Date.now())

  useEffect(() => {
    if (perScreenMinSeconds <= 0) return
    const id = window.setInterval(() => setScreenNowMs(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [perScreenMinSeconds])

  const perScreenAdvance = useMemo(() => {
    if (perScreenMinSeconds <= 0) return { isLocked: false, label: '' }
    const elapsed = Math.floor((screenNowMs - screenStartedAtMs) / 1000)
    const remaining = Math.max(0, perScreenMinSeconds - elapsed)
    const minutes = Math.floor(remaining / 60)
    const seconds = remaining % 60
    return {
      isLocked: remaining > 0,
      label: `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
  }, [perScreenMinSeconds, screenNowMs, screenStartedAtMs])

  const stories = useMemo(() => {
    const all: Story[] = []
    for (const ch of orderedChallenges) {
      const meta = ch.meta
      const chunk = ch.challenge_options?.length
        ? ch.challenge_options.map(opt => ({
            kind: (opt.meta?.kind ?? 'image') as Story['kind'],
            text: opt.meta?.text,
            image: opt.meta?.image,
            video: opt.meta?.video
          }))
        : ((_.get(meta, 'stories', []) as Story[]) ?? [])
      all.push(...chunk)
    }
    return all
  }, [orderedChallenges])
  const [showHandClickAnimation, setShowHandClickAnimation] = useState(false)
  const [hasCompletedStories, setHasCompletedStories] = useState(false)
  const [isUserPaused, setIsUserPaused] = useState(false)
  const [isFinishing, setIsFinishing] = useState(false)
  const storyIndexRef = useRef(0)

  const isStoriesPaused = resolveStoriesPlaybackPaused(
    perScreenAdvance.isLocked || minimumAdvance.isLocked,
    isUserPaused
  )

  const storiesData = useMemo(
    () =>
      stories.map(story => {
        if (story.kind === 'text') {
          return {
            content: () => (
              <div
                className="w-full h-full bg-white text-2xl px-6 py-16 flex items-center justify-center"
                dangerouslySetInnerHTML={{ __html: story?.text || '' }}
              />
            )
          }
        }
        if (story.kind === 'image') {
          return {
            content: () => (
              <img
                className="w-full h-full object-contain object-center block"
                src={story?.image?.file?.url}
                alt={story.kind}
              />
            )
          }
        }
        if (story.kind === 'video') {
          return {
            url: story?.video?.file?.url,
            type: 'video'
          }
        }
        return {}
      }),
    [stories]
  )

  const completeChallenge = useCallback(async () => {
    if (!orderedChallenges.length) return
    if (isFinishing) return

    try {
      setIsFinishing(true)
      for (const ch of orderedChallenges) {
        const { ok } = await onChallengeAnswer({
          uid: ch.uid,
          answer: 'Finished',
          skipValidation: true
        })
        if (!ok) return
      }
      goNext()
    } catch (error) {
      showErrorAlert(error)
    } finally {
      setIsFinishing(false)
    }
  }, [goNext, isFinishing, onChallengeAnswer, orderedChallenges])

  const handleComplete = () => {
    setHasCompletedStories(true)
    if (!minimumAdvance.isLocked) void completeChallenge()
  }

  useEffect(() => {
    if (!hasCompletedStories || minimumAdvance.isLocked) return
    void completeChallenge()
  }, [completeChallenge, hasCompletedStories, minimumAdvance.isLocked])

  useEffect(() => {
    setHasCompletedStories(false)
    setShowHandClickAnimation(false)
    setIsUserPaused(false)
    setIsFinishing(false)
    storyIndexRef.current = 0
    const now = Date.now()
    setScreenStartedAtMs(now)
    setScreenNowMs(now)
  }, [step.id])

  if (!displayChallenge) return null

  return (
    <HUD.Card>
      <HUD.Header challenge={displayChallenge} />
      <HUD.Body noClasses className="rounded-b-xl overflow-hidden row-span-2">
        <div className="flex justify-center items-center relative h-full w-full">
          <div className="z-[1] h-full w-full inset-0 relative">
            <Stories
              key={step.id}
              height="100%"
              width="100%"
              stories={storiesData}
              defaultInterval={perScreenMinSeconds > 0 ? 86400000 : 15000}
              isPaused={isStoriesPaused}
              preventDefault={perScreenAdvance.isLocked}
              onStoryStart={(idx: number) => {
                storyIndexRef.current = idx
                const now = Date.now()
                setScreenStartedAtMs(now)
                setScreenNowMs(now)
              }}
              onPrevious={() => {
                if (storyIndexRef.current === 0) {
                  goPrev()
                }
              }}
              onNext={() =>
                !showHandClickAnimation && setShowHandClickAnimation(true)
              }
              onAllStoriesEnd={handleComplete}
              progressWrapperStyles={{
                height: 6
              }}
              progressStyles={{
                height: 6
              }}
              preloadCount={2}
            />
            {perScreenAdvance.isLocked && (
              <div
                className="absolute inset-0 z-[1000] cursor-not-allowed"
                onClick={e => { e.preventDefault(); e.stopPropagation() }}
                onPointerDown={e => { e.preventDefault(); e.stopPropagation() }}
                onTouchStart={e => { e.preventDefault(); e.stopPropagation() }}
                onTouchMove={e => { e.preventDefault(); e.stopPropagation() }}
                onMouseDown={e => { e.preventDefault(); e.stopPropagation() }}
                onMouseUp={e => { e.preventDefault(); e.stopPropagation() }}
                onTouchEnd={e => { e.preventDefault(); e.stopPropagation() }}
              />
            )}
          </div>
          <StoryPlaybackControl
            isPaused={isUserPaused}
            disabled={perScreenAdvance.isLocked || minimumAdvance.isLocked}
            onToggle={() => setIsUserPaused(current => !current)}
          />
        </div>
      </HUD.Body>
      <LevelFooter>
        <Button type="button" className="w-full font-bold" disabled>
          {perScreenAdvance.isLocked
            ? `Aguarde ${perScreenAdvance.label}`
            : minimumAdvance.isLocked
            ? `Aguarde ${minimumAdvance.label}`
            : 'Aguardando conclusão dos stories'}
        </Button>
      </LevelFooter>
    </HUD.Card>
  )
}
